import React, { useEffect, useState } from 'react';
import { Box, Divider, Typography, MenuItem, TableRow } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import SqlEditor from '../components/SqlEditor';
import FavoritesDrawer from '../components/FavoritesDrawer';
import QueryResultsTable from '../components/QueryResultsTable';
import QueryToolbar from '../components/QueryToolbar';
import { CustomerMassQueryResult, NewFavoriteQuery } from '../types/query';
import { CardWrapper, PaperWrapper } from '../../Common/CommonComponents';
import { CustomSelect } from '../../../../Common/Components/Controls/InputControls';
import {
  CheckBoxItem,
  Product,
  baseFilterOptions,
} from '../../../Common/CommonModel';
import { capitalize } from '../../../../Helpers/UtilityMethods';
import { useFarmStyles as commonStyle } from '../../../../Common/Styles';
import { Loader } from '../../../../Common/Components/Loader';
import { Customer } from '../../Customer/Model/CustomerModel';
import { downloadExcelWithSheets } from '../utils/csvDownloader';
import { CustomerFilter } from '../../../Common/Filters/CustomerFilter';
import { useProducts } from '../hooks/useProducts';
import { useInitData } from '../hooks/useInitData';
import { useCustomerIds } from '../hooks/useCustomerIds';
import { useExecuteQuery } from '../hooks/useExecuteQuery';
import { useBaseFilter } from '../../../Common/Filters/Hooks/useBaseFilter';
import { showToast } from '../../../../lib/toast';
import { useTranslation } from 'react-i18next';

const SqlEditorPage = () => {
  const { classes } = commonStyle();
  const { t } = useTranslation('sqlengine');

  // Query states
  const [favoriteQuery, setFavoriteQuery] = useState<NewFavoriteQuery>({
    Id: '00000000-0000-0000-0000-000000000000',
    QueryName: '',
    QueryText: '',
  });
  const [results, setResults] = useState<CustomerMassQueryResult>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Drawer and visibility states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCustomerFilterVisible, setIsCustomerFilterVisible] = useState(false);

  // Customer-related states
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<Customer>();

  // Filter options
  const [filterOption, setFilterOption] = useState('');
  const [productOption, setProductOption] = useState('');
  const [farmOptions, setFarmOptions] = useState<CheckBoxItem[]>([]);
  const [customerOptions, setCustomerOptions] = useState<CheckBoxItem[]>([]);
  const [tagOptions, setTagOptions] = useState<CheckBoxItem[]>([]);

  // Product data
  const [products, setProducts] = useState<Product[]>([]);

  useProducts(setLoading, setProducts, setError);
  useInitData({
    setLoading,
    setError,
    setCustomers,
    setIsCustomerFilterVisible,
    setFarmOptions,
    setCustomerOptions,
    setTagOptions,
    productOption,
  });

  const customerIds = useCustomerIds({
    filterOption,
    customers,
    customerOptions,
    farmOptions,
    tagOptions,
    setLoading,
    setError,
    setFilteredCustomers,
    setCustomer,
  });

  const baseFilters = useBaseFilter({
    farmCheckBoxList: farmOptions,
    setFarmCheckBoxList: setFarmOptions,
    customerCheckBoxList: customerOptions,
    setCustomerCheckBoxList: setCustomerOptions,
    tagCheckBoxList: tagOptions,
    setTagCheckBoxList: setTagOptions,
  });

  const { handleExecute } = useExecuteQuery({
    favoriteQuery,
    customerIds,
    setResults,
    setError,
    setLoading,
  });

  const handleChange = (value: string) => {
    setFavoriteQuery({ ...favoriteQuery, QueryText: value });
  };

  const handleDownload = () => {
    downloadExcelWithSheets(results, filteredCustomers);
  };

  useEffect(() => {
    if (error) {
      showToast({
        title: t(error as string),
        type: 'error',
      });
      setError('');
    }
  }, [error, t]);

  return (
    <PaperWrapper>
      <Typography variant="h5" gutterBottom>
        SQL Engine
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 8 }}>
          <CardWrapper>
            <Typography variant="h6">Basic Filters</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="body1">Product</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  value={productOption}
                  onChange={(e) => {
                    setProductOption(e.target.value);
                  }}
                  select
                >
                  {products.map((product) => (
                    <MenuItem key={product.Id} value={product.Id}>
                      {product.Id}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid2>
            </Grid2>

            {isCustomerFilterVisible && (
              <Grid2 container spacing={2} alignItems="center" mt={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body1">Filter</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomSelect
                    value={filterOption}
                    onChange={(e) => setFilterOption(e.target.value)}
                  >
                    {baseFilterOptions.map((to) => (
                      <MenuItem key={to.Text} value={to.Value}>
                        {to.Text}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid2>
              </Grid2>
            )}
          </CardWrapper>

          <CardWrapper sx={{ mt: 2 }}>
            <Typography variant="h6">Sql Editor</Typography>
            <Divider sx={{ mb: 3 }} />
            <QueryToolbar
              onExecute={handleExecute}
              onDownload={handleDownload}
              onToggleFavorites={() => setDrawerOpen(true)}
              loading={loading}
              setFavoriteQuery={setFavoriteQuery}
              favoriteQuery={favoriteQuery}
              setError={setError}
            />
            <SqlEditor
              query={favoriteQuery.QueryText}
              onChange={handleChange}
            />
          </CardWrapper>
        </Grid2>

        <Loader loading={loading} />

        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          {filterOption && filterOption !== 'allcustomers' && (
            <CardWrapper>
              <Typography variant="h6">{capitalize(filterOption)}</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box className={classes.box}>
                <TableRow>
                  {filterOption && (
                    <CustomerFilter
                      FilterMapping={baseFilters}
                      filterOption={filterOption}
                    />
                  )}
                </TableRow>
              </Box>
            </CardWrapper>
          )}

          {filteredCustomers.length > 0 && (
            <CardWrapper sx={{ mt: 2 }}>
              <Typography variant="h6">Result View</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box className={classes.box}>
                <CustomSelect
                  value={customer?.Id ?? ''}
                  onChange={(e) => {
                    const selectedCustomer = filteredCustomers.find(
                      (cust) => cust.Id === e.target.value,
                    );
                    setCustomer(selectedCustomer);
                  }}
                >
                  {filteredCustomers.map((customer) => (
                    <MenuItem key={customer.Id} value={customer.Id}>
                      {customer?.Subdomain}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Box>
            </CardWrapper>
          )}
        </Grid2>
      </Grid2>

      {customer && (
        <Box mt={4}>
          <QueryResultsTable data={results} customer={customer} />
        </Box>
      )}

      <FavoritesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSelectQuery={(selectedQuery) => {
          setFavoriteQuery(selectedQuery);
          setDrawerOpen(false);
        }}
        setError={setError}
      />
    </PaperWrapper>
  );
};

export default SqlEditorPage;
